import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter,
  Button, 
  OutlineButton, 
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  ErrorText,
  Grid
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';

import { AccountingEntry, AccountingType } from '../../types';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';

const BackButton = styled(OutlineButton)`
  margin-bottom: 1rem;
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

interface FormErrors {
  description?: string;
  amount?: string;
  type?: string;
  category?: string;
  account?: string;
  date?: string;
}

const FinanceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<{
    description: string;
    amount: string;
    currency: string;
    type: AccountingType;
    category: string;
    account: string;
    date: string;
    reference: string;
    projectId: string;
    attachments: File[];
  }>({
    description: '',
    amount: '',
    currency: 'TZS',
    type: 'Expense',
    category: 'Office Expenses',
    account: 'Administrative Expenses',
    date: new Date().toISOString().split('T')[0],
    reference: '',
    projectId: '',
    attachments: []
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(isEditMode);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogType, setDialogType] = useState<'success' | 'error'>('success');
  
  // Check if current user can manage finances
  const canManageFinances = user?.role === 'MD' || user?.role === 'TD' || user?.role === 'Accountant';
  
  // Load projects for project selection
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await apiService.getProjects({ limit: 1000 });
        setProjects(response.data || []);
      } catch (error) {
        console.error('Error loading projects:', error);
      }
    };
    
    if (user) {
      loadProjects();
    }
  }, [user]);
  
  const showSuccessDialog = (message: string) => {
    setDialogMessage(message);
    setDialogType('success');
    setShowDialog(true);
  };
  
  const showErrorDialog = (message: string) => {
    setDialogMessage(message);
    setDialogType('error');
    setShowDialog(true);
  };

  useEffect(() => {
    if (!canManageFinances) {
      // Redirect if user doesn't have permission
      navigate('/office');
      return;
    }
    
    if (isEditMode && id) {
      // Load real data from API
      const loadEntryData = async () => {
        try {
          setLoading(true);
          const response = await apiService.getAccountingEntry(id);
          if (response.success && response.data) {
            const entryData = response.data;
            setFormData({
              description: entryData.description,
              amount: entryData.amount.toString(),
              currency: entryData.currency,
              type: entryData.type,
              category: entryData.category,
              account: entryData.account,
              date: new Date(entryData.date).toISOString().split('T')[0],
              reference: entryData.reference || '',
              projectId: entryData.projectId || '',
              attachments: []
            });
          } else {
            // Entry not found
            navigate('/office');
          }
        } catch (error) {
          console.error('Error loading accounting entry:', error);
          navigate('/office');
        } finally {
          setLoading(false);
        }
      };
      
      loadEntryData();
    } else {
      setLoading(false);
    }
  }, [id, isEditMode, canManageFinances, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newFiles]
      }));
    }
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Required fields
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be a positive number';
    }
    
    if (!formData.type) {
      newErrors.type = 'Type is required';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.account.trim()) {
      newErrors.account = 'Account is required';
    }
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      // Prepare entry data
      const entryData = {
        description: formData.description,
        amount: Number(formData.amount),
        currency: formData.currency,
        type: formData.type,
        category: formData.category,
        account: formData.account,
        date: new Date(formData.date),
        reference: formData.reference || undefined,
        projectId: formData.projectId || undefined,
        createdBy: user?.name || 'System User'
      };
      
      if (isEditMode) {
        await apiService.updateAccountingEntry(id!, entryData);
        showSuccessDialog('Entry updated successfully!');
      } else {
        await apiService.createAccountingEntry(entryData);
        showSuccessDialog('Entry created successfully!');
      }
      
      // Navigate after a short delay to show the success message
      setTimeout(() => {
        navigate('/office');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving accounting entry:', error);
      showErrorDialog(`Failed to ${isEditMode ? 'update' : 'create'} entry: ${error.message}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <BackButton as={Link} to="/office">
        &larr; Back to Office Finance
      </BackButton>
      
      <h1 className="page-title">{isEditMode ? 'Edit Financial Entry' : 'Add New Financial Entry'}</h1>
      
      <FormContainer>
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <h3>Entry Details</h3>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label htmlFor="description">Description *</Label>
                <TextArea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className={errors.description ? 'error' : ''}
                />
                {errors.description && <ErrorText>{errors.description}</ErrorText>}
              </FormGroup>
              
              <Grid columns="1fr 1fr" gap="1rem">
                <FormGroup>
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={handleChange}
                    className={errors.amount ? 'error' : ''}
                  />
                  {errors.amount && <ErrorText>{errors.amount}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="currency">Currency *</Label>
                  <Select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                  >
                    <option value="TZS">TZS</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="type">Type *</Label>
                  <Select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className={errors.type ? 'error' : ''}
                  >
                    <option value="Income">Income</option>
                    <option value="Expense">Expense</option>
                    <option value="Asset">Asset</option>
                    <option value="Liability">Liability</option>
                    <option value="Equity">Equity</option>
                  </Select>
                  {errors.type && <ErrorText>{errors.type}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    className={errors.date ? 'error' : ''}
                  />
                  {errors.date && <ErrorText>{errors.date}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="category">Category *</Label>
                  <Input
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={errors.category ? 'error' : ''}
                  />
                  {errors.category && <ErrorText>{errors.category}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="account">Account *</Label>
                  <Input
                    id="account"
                    name="account"
                    value={formData.account}
                    onChange={handleChange}
                    className={errors.account ? 'error' : ''}
                  />
                  {errors.account && <ErrorText>{errors.account}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="reference">Reference</Label>
                  <Input
                    id="reference"
                    name="reference"
                    value={formData.reference}
                    onChange={handleChange}
                    placeholder="e.g., Invoice number, Receipt number"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="projectId">Project (Optional)</Label>
                  <Select
                    id="projectId"
                    name="projectId"
                    value={formData.projectId}
                    onChange={handleChange}
                  >
                    <option value="">Select a project (optional)</option>
                    {projects.map(project => (
                      <option key={project._id || project.id} value={project._id || project.id}>
                        {project.name}
                      </option>
                    ))}
                  </Select>
                  <small style={{ color: 'var(--dark-gray)' }}>
                    Link this entry to a specific project (optional)
                  </small>
                </FormGroup>
              </Grid>
            </CardBody>
          </Card>
          
          <Card style={{ marginTop: '1.5rem' }}>
            <CardHeader>
              <h3>Attachments</h3>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label htmlFor="attachments">Upload Documents</Label>
                <Input
                  id="attachments"
                  name="attachments"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                />
                <small style={{ color: 'var(--dark-gray)' }}>
                  Supported formats: PDF, JPG, PNG. Max size: 5MB per file.
                </small>
              </FormGroup>
              
              {formData.attachments.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <Label>Selected Files</Label>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {formData.attachments.map((file, index) => (
                      <li key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '0.5rem',
                        borderBottom: '1px solid var(--light-gray)'
                      }}>
                        <span>{file.name} ({(file.size / 1024).toFixed(2)} KB)</span>
                        <Button 
                          type="button" 
                          onClick={() => removeAttachment(index)}
                          style={{ backgroundColor: 'var(--danger-color)', padding: '4px 8px', fontSize: '0.85rem' }}
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardBody>
            
            <CardFooter>
              <OutlineButton as={Link} to="/office" type="button">
                Cancel
              </OutlineButton>
              <Button type="submit" disabled={submitLoading}>
                {submitLoading ? 'Saving...' : isEditMode ? 'Update Entry' : 'Create Entry'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </FormContainer>
      
      {/* Custom Dialog */}
      {showDialog && (
        <CustomDialog
          isOpen={showDialog}
          onClose={() => setShowDialog(false)}
          title={dialogType === 'success' ? 'Success' : 'Error'}
          message={dialogMessage}
          type={dialogType}
        />
      )}
    </div>
  );
};

export default FinanceForm; 